<template>
  <v-touch
    ref="touch"
    @singletap="tapToggleReadMode"
    @doubletap="zoomToggle"
    @panstart="handlePan"
    @panmove="handlePan"
    @panend="handlePan($event, true)"
    @pinchstart="handlePinch"
    @pinchmove="handlePinch"
    @pinchend="handlePinch($event, true)"
    @swipeleft="handleSwipe"
    @swiperight="handleSwipe"
  >
    <div
      :style="style"
      @wheel="handleWheel"
      @pointerdown="pointerDown"
      @pointerup="pointerUp"
    >
      <slot></slot>
    </div>
  </v-touch>
</template>

<script>
import {Power3, TweenLite} from 'gsap/umd/TweenLite'
import EventListenerMixin from './EventListenerMixin.vue'

const CENTER_HORIZONTALLY = true
const CENTER_VERTICALLY = false
const MAX_ZOOM = 4
const MIN_ZOOM = 1

export default {
  name: 'Zoomable',

  mixins: [EventListenerMixin],

  props: {
    containerHeight: {
      type: Number,
      required: true,
    },
    containerWidth: {
      type: Number,
      required: true,
    },
    contentHeight: {
      type: Number,
      required: true,
    },
    contentWidth: {
      type: Number,
      required: true,
    },
  },

  inject: ['pageNext', 'pagePrevious'],

  data() {
    return {
      // How should zoom default to fit the page on the screen.
      //   width -- fit the page width
      //   both -- fit the full page
      //   auto -- choose between both and width by device characteristics
      fitDirection: 'auto',

      // These are controlled by setters below to enforce min/max.
      controlled: {
        panX: 0,
        panY: 0,
        zoom: null, // falls back to this.zoomToFit
      },

      // Pan and pinch events report as a delta from the start of the
      // operation, so a sequence of events is a cumulative delta until
      // the operation completes. When either of these is non-null, it
      // means that there is an operation in progress, and we're tracking
      // the start of the operation until it completes.
      panStart: null,
      swipeStart: null,
      zoomStart: null,

      // Tween in progress.
      tween: null,
      killedTween: false,

      // Transition for keyboard panning.
      transition: null,
    }
  },

  mounted() {
    // Finish setting up singletap and doubletap v-touch custom events.
    // https://github.com/vuejs/vue-touch/issues/70
    // http://hammerjs.github.io/require-failure/
    // https://github.com/hammerjs/hammer.js/issues/1010
    const vt = this.$refs.touch

    // Under test with shallowMount, this isn't a real vue-touch
    // component so recognizers isn't available.
    if (process.env.NODE_ENV !== 'test' || vt.recognizers !== undefined) {
      vt.recognizers['doubletap'].recognizeWith('singletap')
      vt.recognizers['singletap'].requireFailure('doubletap')
      vt.recognizers['doubletap'].dropRequireFailure('singletap')

      // PageLink handles taps to follow links, but because of how
      // vue-touch/hammerjs is implemented--translating raw pointer
      // events to high-level events via recognizers--those raw events
      // end up here too. Avoid treating them as singletap/doubletap.
      vt.recognizers['singletap'].set({
        enable: (recog, event) => !event || !event.srcEvent.iGotThis,
      })
      vt.recognizers['doubletap'].set({
        enable: (recog, event) => !event || !event.srcEvent.iGotThis,
      })
    }

    this.$addEventListener(this.$el, 'transitionend', () => {
      this.transition = null
    })
  },

  computed: {
    panX: {
      get: function () {
        return this.controlled.panX
      },
      set: function (panX) {
        if (panX !== this.controlled.panX) {
          this.controlled = {...this.controlled, panX}
        }
        this.enforceLimits()
      },
    },

    panY: {
      get: function () {
        return this.controlled.panY
      },
      set: function (panY) {
        if (panY !== this.controlled.panY) {
          this.controlled = {...this.controlled, panY}
        }
        this.enforceLimits()
      },
    },

    zoom: {
      get: function () {
        return this.controlled.zoom || this.zoomToFit
      },
      set: function (zoom) {
        if (zoom !== this.controlled.zoom) {
          this.controlled = {...this.controlled, zoom}
        }
        this.enforceLimits()
      },
    },

    scale() {
      // Fit full page at zoom=1
      return Math.min(
        this.containerWidth / this.contentWidth,
        this.containerHeight / this.contentHeight,
      )
    },

    zoomToFit() {
      if (
        this.fitDirection === 'width' ||
        (this.fitDirection === 'auto' &&
          (this.containerHeight < 400 || this.containerWidth < 700))
      ) {
        return this.zoomToFitWidth
      }
      return this.zoomToFitPage
    },

    zoomToFitPage() {
      // Fit the full page with zoom=1. This works because of combining
      // with this.scale
      return 1
    },

    zoomToFitWidth() {
      // Fit the page width. This is weird because it has to account for
      // whether this.scale is currently based on height or width.
      return (
        Math.max(this.scale, this.containerWidth / this.contentWidth) /
        this.scale
      )
    },

    maxX() {
      return this.contentWidth * this.zoom * this.scale - this.containerWidth
    },

    maxY() {
      return this.contentHeight * this.zoom * this.scale - this.containerHeight
    },

    offsetX() {
      return CENTER_HORIZONTALLY
        ? Math.max(
            0,
            (this.containerWidth - this.contentWidth * this.zoom * this.scale) /
              2,
          )
        : 0
    },

    offsetY() {
      return CENTER_VERTICALLY
        ? Math.max(
            0,
            (this.containerHeight -
              this.contentHeight * this.zoom * this.scale) /
              2,
          )
        : 0
    },

    style() {
      const translateX = -this.panX + this.offsetX
      const translateY = -this.panY + this.offsetY
      const scale = this.zoom * this.scale
      return {
        transform: `
                    translateX(${translateX}px)
                    translateY(${translateY}px)
                    scale(${scale})`,
        transformOrigin: 'top left',

        // Only transition for keyboard events, not touch.
        transition: this.transition,

        // Additionally provide a relatively positioned and
        // sized div so subcomponents can absolutely position
        // themselves, for example PageLink.
        position: 'relative',
        height: `${this.contentHeight}px`,
        width: `${this.contentWidth}px`,
      }
    },
  },

  watch: {
    zoom(z) {
      this.$depub('zoomed', z)
    },
  },

  beforeDestroy() {
    this.killTween()
  },

  methods: {
    enforceLimits() {
      // Enforce zoom first, since it affects max panning
      if (this.controlled.zoom !== null) {
        const zoom = this.limitedZoom(this.controlled.zoom)
        if (zoom !== this.controlled.zoom) {
          this.controlled = {...this.controlled, zoom}
        }
      }

      // Enforce min/max panning
      const panX = Math.max(0, Math.min(this.maxX, this.controlled.panX))
      const panY = Math.max(0, Math.min(this.maxY, this.controlled.panY))
      if (panX !== this.controlled.panX || panY !== this.controlled.panY) {
        this.controlled = {...this.controlled, panX, panY}
      }
    },

    limitedZoom(zoom) {
      return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom))
    },

    handlePan(event, end = false, zoomOffset = null) {
      if (!this.panStart && !end) {
        this.panStart = {
          panX: this.panX,
          panY: this.panY,

          // Normalize deltaX and deltaY to zero since a panstart can
          // follow immediately from pinchend by lifting one finger,
          // and in that case deltas aren't cleared.
          deltaX: event.deltaX,
          deltaY: event.deltaY,
        }

        // Track the start of the pan sequence also as the start of a
        // swipe, so that if we get a swipe event at the end, we know
        // where it started.
        this.swipeStart = this.panStart

        // Accumulate a list of events so we can take the average
        // of the last few velocities for momentum calculation.
        this.velocityEvents = []
      }

      if (this.panStart) {
        const dx = -(event.deltaX - this.panStart.deltaX)
        const dy = -(event.deltaY - this.panStart.deltaY)
        this.panX = this.panStart.panX + dx + (zoomOffset ? zoomOffset.x : 0)
        this.panY = this.panStart.panY + dy + (zoomOffset ? zoomOffset.y : 0)
        this.velocityEvents.push(event)
      }

      if (end) {
        this.panStart = null
        this.doMomentum()
      }
    },

    doMomentum() {
      const velocityFrames = 4
      const velocityWindow = 100 // ms
      const directionThreshold = 2
      const inertiaThreshold = 0.25
      const tweenDuration = 2.5

      // Filter this.velocityEvents by velocityFrames (max number
      // of events) and velocityWindow (max time delta from last
      // event)
      const lastTimeStamp =
        this.velocityEvents[this.velocityEvents.length - 1].timeStamp
      const events = this.velocityEvents
        .slice(-velocityFrames)
        .filter(e => e.timeStamp >= lastTimeStamp - velocityWindow)

      // Calculate starting velocities for tweening
      const avg = (arr, k) => {
        const sum = arr.map(e => e[k]).reduce((sum, v) => sum + v)
        return sum / arr.length
      }
      let velocity = {
        x: avg(events, 'velocityX'),
        y: avg(events, 'velocityY'),
      }

      // Refrain from applying momentum for slow pans.
      const absX = Math.abs(velocity.x)
      const absY = Math.abs(velocity.y)
      if (absX < inertiaThreshold && absY < inertiaThreshold) {
        return
      }

      // Lock momentum vertically or horizontally when it's close,
      // to avoid unintentional slight diagonal momentum.
      if (absX !== 0 && absY / absX > directionThreshold) {
        velocity.x = 0
      } else if (absY !== 0 && absX / absY > directionThreshold) {
        velocity.y = 0
      }

      const z = this // since this in onUpdate refers to tween
      let lastTime = 0

      this.tween = TweenLite.to(velocity, tweenDuration, {
        x: 0,
        y: 0,
        ease: Power3.easeOut,
        onUpdate() {
          const thisTime = this.time()
          const elapsed = thisTime - lastTime
          lastTime = thisTime
          const oldX = z.panX
          const oldY = z.panY
          z.panX -= velocity.x * elapsed * 1000
          z.panY -= velocity.y * elapsed * 1000

          // Stop when we hit the page edge
          if (
            elapsed &&
            ((velocity.y && z.panY === oldY) || (velocity.x && z.panX === oldX))
          ) {
            this.kill()
          }
        },
      })
    },

    // hammerjs/vue-touch works with pointer events, and tap events are
    // ultimately triggered by pointerup. These two handlers work
    // together to avoid triggering single/double-tap when the tap is
    // cancelling pan momentum.
    pointerDown() {
      this.killedTween = this.killTween()
    },

    pointerUp(event) {
      if (this.killedTween) {
        event.iGotThis = true
        this.killedTween = false
      }
    },

    killTween() {
      const active = !!this.tween && this.tween.isActive()
      if (active) {
        this.tween.kill()
      }
      this.tween = null
      return active
    },

    handlePinch(event, end = false) {
      if (!end) {
        if (!this.zoomStart) {
          this.zoomStart = {
            zoom: this.zoom,
            center: event.center,
          }
        }
        // Call this.handlePan to set up panStart too, since we need it
        // for calculating center points.
        this.handlePan(event)
      }

      if (this.zoomStart) {
        // Calculate the x/y offsets needed to keep zoom centered
        // on pinch. Note that event.scale is relative to the zoom
        // at pinchstart.
        const newZoom = this.zoomStart.zoom * event.scale
        const zoomOffset = this.zoomOffset(newZoom, this.zoomStart.center, {
          ...this.panStart,
          ...this.zoomStart,
        })

        // Set the new zoom. This is constrained min/max by the
        // setter.
        this.zoom = newZoom

        // Hand off to handlePan to finish handling this event, since we
        // now have zoomOffset which will be added to the event.deltaX
        // and event.deltaY
        this.handlePan(event, end, zoomOffset)
      }

      if (end) {
        this.zoomStart = null
      }
    },

    /**
     * Calculate the x/y offsets to keep a point in its
     * viewport-relative position between start.zoom and zoom.
     *
     * Point is viewport-relative because that's what we get in
     * event.center for touch events. This means that calculating an
     * element-relative coordinate must take into account that
     * element's position within the viewport.
     *
     * @param {number} zoom - new requested zoom, will be limited min/max
     * @param {Object} point - x/y viewport-relative coordinates of point
     * @param {Object} start - zoom/panX/panY starting values
     * @returns {Object} x/y offsets
     */
    zoomOffset(zoom, point, start) {
      // Enforce limit on zoom before calculation, since zoomOffset
      // might be called on a requested zoom before attempting to
      // apply to this.zoom
      zoom = this.limitedZoom(zoom)

      // Point is viewport-relative, not element relative, so we need
      // the left and top values for the viewer within the viewport.
      const viewerRect = this.$refs.touch.$el.getBoundingClientRect()

      // Calculate this point relative to contentWidth and
      // contentHeight.
      const absolutePoint = {
        x: start.panX + point.x - viewerRect.left - this.offsetX,
        y: start.panY + point.y - viewerRect.top - this.offsetY,
      }

      // Calculate what this point will be, relative to contentWidth
      // and contentHeight, after adjusting the zoom.
      const zoomedPoint = {
        x: (absolutePoint.x * zoom) / start.zoom,
        y: (absolutePoint.y * zoom) / start.zoom,
      }

      // Subtract old from new to get the x/y offsets.
      return {
        x: zoomedPoint.x - absolutePoint.x,
        y: zoomedPoint.y - absolutePoint.y,
      }
    },

    /**
     * Adjust zoom, keeping point in its viewport-relative location.
     *
     * @param {number} zoom - requested zoom, will be limited min/max
     * @param {Object} point - viewport-relative x/y coordinates
     */
    zoomOnPoint(zoom, point) {
      const offset = this.zoomOffset(zoom, point, this)
      this.controlled = {
        ...this.controlled,
        zoom: zoom,
        panX: this.panX + offset.x,
        panY: this.panY + offset.y,
      }
      this.enforceLimits()
    },

    handleSwipe(event) {
      // Any swipe event will be preceded by a sequence of pan events that
      // set this.swipeStart for reference. The check here is just for
      // sanity--never trust events to work as expected.
      if (this.swipeStart && this.swipeStart.panX === this.panX) {
        if (event.type === 'swipeleft') {
          this.pageNext()
        } else {
          this.pagePrevious()
        }
      }
    },

    handleWheel(event) {
      let {deltaX, deltaY, shiftKey} = event

      // Allow shift-wheel to scroll horizontally, but make sure it's not
      // already a horizontal scroll event.
      if (shiftKey && !deltaX) {
        deltaX = deltaY
      }

      // console.log() says the default wheel scroll deltas are:
      //   DOM_DELTA_LINE on Firefox: 3
      //   DOM_DELTA_PIXEL on Brave: 79.5
      // so normalize to pixels, since that's what we're scrolling.
      const mult = event.deltaMode === 1 ? 79.5 / 3 : 1
      this.panX += deltaX * mult
      this.panY += deltaY * mult
    },

    zoomToggle() {
      // If the page is already fitted to the current fitDirection,
      // toggle fitDirection to the other value.
      if (this.zoom === this.zoomToFit) {
        this.fitDirection = this.fitDirection === 'auto' ? 'width' : 'auto'
      }

      // Setting to null causes this.zoom to fall back to computed
      // this.zoomToFit
      this.zoom = null
    },

    // called by parent
    zoomIn(step = 0.5) {
      const nextZoom = this.zoom + step
      if (this.zoom < this.zoomToFitWidth && nextZoom >= this.zoomToFitWidth) {
        this.fitDirection = 'width'
        this.zoom = null
      } else {
        this.zoom = nextZoom
      }
    },

    // called by parent
    zoomOut(step = 0.5) {
      const nextZoom = this.zoom - step
      if (this.zoom > this.zoomToFitWidth && nextZoom <= this.zoomToFitWidth) {
        this.fitDirection = 'width'
        this.zoom = null
      } else {
        this.zoom = nextZoom
      }
    },

    // called by parent
    setZoom(zoom) {
      this.zoom = zoom
    },

    panTo({x, y, dx, dy, smooth = true}) {
      const old = this.controlled
      if (x !== undefined) {
        this.panX = this.maxX * x
      } else if (dx) {
        this.panX += this.containerWidth * dx
      }
      if (y !== undefined) {
        this.panY = this.maxY * y
      } else if (dy) {
        this.panY += this.containerHeight * dy
      }
      if (this.panX !== old.panX || this.panY !== old.panY) {
        if (smooth) {
          this.transition = 'transform 0.1s linear'
        }
        return true
      }
      return false
    },

    tapToggleReadMode(event) {
      if (event.pointerType === 'touch') {
        this.$pub('toggleReadMode')
      }
    },
  },
}
</script>
